import {
  Heading,
  Box,
  Badge,
  SkeletonCircle,
  SkeletonText,
  GridItem,
  Flex,
  Image,
  Text,
  Wrap,
  WrapItem,
  Grid,
  Container,
  useToast,
  VStack,
  useColorModeValue,
  Button,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  ModalFooter,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BriefcaseIcon,
  CurrencyRupeeIcon,
  BuildingOffice2Icon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import {
  fetchAdminJobListAsync,
  selectAdminJobList,
  selectAdminJobListTotal,
} from "./AdminJobListSlice";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../common/URLs";
import { TimeDifference } from "../../common/TimeDifference";

export function AdminJobList() {
  const dispatch = useDispatch();
  const jobs = useSelector(selectAdminJobList);
  const totalJobs = useSelector(selectAdminJobListTotal);

  const jobsPerPage = 15; // Number of jobs per page
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate total pages
  const totalPages = Math.ceil(totalJobs / jobsPerPage);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };
  const [isLoading, setIsLoading] = useState(true);

  // Fetch jobs whenever the current page changes
  useEffect(() => {
    dispatch(fetchAdminJobListAsync(currentPage))
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [dispatch, currentPage]);

  const [bookmarkedJobs, setBookmarkedJobs] = useState({});

  const [appliedJobs, setAppliedJobs] = useState({});

  // Prefill applied jobs state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialAppliedJobs = {};
      jobs.forEach((job) => {
        initialAppliedJobs[job.uuid] = job.candidate_job_apply_count === 1;
      });
      setAppliedJobs(initialAppliedJobs);
    }
  }, [jobs]);

  // Prefill bookmark state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialBookmarks = {};
      jobs.forEach((job) => {
        initialBookmarks[job.uuid] = job.candidate_job_saved_count
          ? true
          : false;
      });
      setBookmarkedJobs(initialBookmarks);
    }
  }, [jobs]); // Run when jobs updates

  const toast = useToast();

  const handleBookmarkClick = async (jobId) => {
    const isCurrentlyBookmarked = bookmarkedJobs[jobId]; // Check current status
    const newStatus = isCurrentlyBookmarked ? "remove" : "save"; // Toggle between "save" and "remove"

    setBookmarkedJobs((prev) => ({
      ...prev,
      [jobId]: !isCurrentlyBookmarked, // Optimistic UI update
    }));

    // console.log("Job ID:", jobId, "New Status:", newStatus);

    try {
      // await dispatch(
      //   updateBookmarkAsync({ job_uuid: jobId, status: newStatus })
      // );

      // Show success toast on successful execution
      toast({
        title: `Job ${
          newStatus === "save" ? "saved" : "removed"
        } successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to update bookmark:", error);

      setBookmarkedJobs((prev) => ({
        ...prev,
        [jobId]: isCurrentlyBookmarked, // Revert on failure
      }));

      // Show error toast on failure
      toast({
        title: "Failed to update bookmark.",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const headerBg = useColorModeValue("blue.500", "blue.900");
  const headerTextColor = useColorModeValue("white", "gray.100");
  const subTextColor = useColorModeValue("blue.100", "blue.200");

  return (
    <div className="contentAdmin bg-adminbg rounded p-4">
      <div
        className="bg-white my-0 rounded-md"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex justify-between">
          <p className="text-xl text-black font-semibold px-3 py-7">
            Admin Jobs List
          </p>
        </div>
        <TableContainer>
          <Table size="sm">
            <Thead className="bg-gray-200">
              <Tr>
                <Th>ID</Th>
                <Th>Job Title</Th>
                <Th>Company</Th>
                <Th>Department</Th>
                <Th>Type</Th>
                <Th>Posted On</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                <Tr>
                  <Td colSpan="8" className="text-center py-4">
                    Loading...
                  </Td>
                </Tr>
              ) : jobs && jobs.length > 0 ? (
                jobs.map((job, index) => (
                  <Tr key={job.uuid}>
                    <Td>{index + 1}</Td>
                    <Td>
                      <Link
                        to={`/jobs/${job.uuid}`}
                        className="text-blue-600 font-medium"
                      >
                        {job?.job_title?.title || job.job_name}
                      </Link>
                    </Td>
                    <Td>{job.company_name || "N/A"}</Td>
                    <Td>{job.department?.title || "N/A"}</Td>
                    <Td>{job.job_type || "N/A"}</Td>
                    <Td>
                      <TimeDifference dateTimeString={job.job_post_date} />
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={job.status === "active" ? "green" : "red"}
                      >
                        {job.status}
                      </Badge>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan="8" className="text-center py-4">
                    No jobs available
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      {/* Pagination */}
      <GridItem colSpan={2} display="flex" justifyContent="center">
        <div className="flex w-full justify-evenly items-center mt-4">
          <button
            className={`bg-gray-300 text-black px-4 py-2 rounded ${
              currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className={`bg-blue-500 text-white px-4 py-2 rounded ${
              currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </GridItem>

      {/* Approval Modal */}
      {/* <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent maxW="2xl" borderRadius="lg" boxShadow="lg">
          <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
            Approve Job
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text fontSize="md" textAlign="center">
              Do you want to approve this job{" "}
              <Text as="span" fontWeight="semibold" color="red.500">
                "{selectedJob?.job_title?.title || selectedJob?.job_name}"
              </Text>
              ?
            </Text>
          </ModalBody>
          <ModalFooter display="flex" gap="2" justifyContent="center">
            <Button colorScheme="blue" w="40" onClick={handleJobApprove}>
              Yes
            </Button>
            <Button
              variant="outline"
              colorScheme="blue"
              w="40"
              onClick={handleJobReject}
            >
              No
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal> */}
    </div>
  );
}
